import React, { useState, useMemo } from 'react';
import { Location } from '../../../types';
import { X, Edit, Trash2, Map, Search, Filter } from 'lucide-react';

interface LocationManagerProps {
  locations: Location[];
  onAddLocation: (location: Omit<Location, 'id' | 'position'>) => Promise<string>;
  onUpdateLocation: (location: Location) => Promise<void>;
  onDeleteLocation: (locationId: string) => Promise<void>;
  onClose: () => void;
  onMoveOnMap: (locationId: string) => void;
}

export const LocationManager: React.FC<LocationManagerProps> = ({
  locations,
  onAddLocation,
  onUpdateLocation,
  onDeleteLocation,
  onClose,
  onMoveOnMap
}) => {
  const [newLocation, setNewLocation] = useState<{
    name: string;
    type: 'classroom' | 'restroom' | 'building' | 'other';
    buildingId?: string;
  }>({
    name: '',
    type: 'classroom'
  });
    const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'classroom' | 'restroom' | 'building' | 'other'>('all');
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [deletingLocationId, setDeletingLocationId] = useState<string | null>(null);

  // Get available buildings (locations of type 'building')
  const buildings = useMemo(() => 
    locations.filter(loc => loc.type === 'building'),
    [locations]
  );

  // Filter locations based on search query and type filter
  const filteredLocations = useMemo(() => {
    return locations.filter(location => {
      const matchesSearch = location.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === 'all' || location.type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [locations, searchQuery, typeFilter]);
  const handleAddLocation = async () => {
    if (newLocation.name.trim()) {
      setIsAdding(true);
      try {
        await onAddLocation({
          name: newLocation.name.trim(),
          type: newLocation.type,
          buildingId: newLocation.type === 'classroom' ? newLocation.buildingId : undefined
        });
        setNewLocation({
          name: '',
          type: 'classroom'
        });
      } catch (error) {
        console.error('Failed to add location:', error);
        // You might want to show a toast notification here
      } finally {
        setIsAdding(false);
      }
    }
  };

  const handleStartEdit = (location: Location) => {
    setEditingLocation(location);
  };
  const handleUpdateLocation = async () => {
    if (editingLocation && editingLocation.name.trim()) {
      setIsUpdating(true);
      try {
        await onUpdateLocation(editingLocation);
        setEditingLocation(null);
      } catch (error) {
        console.error('Failed to update location:', error);
        // You might want to show a toast notification here
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const handleDeleteLocation = async (locationId: string) => {
    if (window.confirm('Are you sure you want to delete this location? This action cannot be undone.')) {
      setDeletingLocationId(locationId);
      try {
        await onDeleteLocation(locationId);
      } catch (error) {
        console.error('Failed to delete location:', error);
        // You might want to show a toast notification here
      } finally {
        setDeletingLocationId(null);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingLocation(null);
  };

  return (
    <div className="bg-white rounded-lg p-6 w-full max-w-3xl">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-stone-800">Manage Locations</h2>
        <button
          onClick={onClose}
          className="text-stone-500 hover:text-stone-700"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      {/* Add new location form */}
      <div className="mb-6 bg-stone-50 p-4 rounded-md">
        <h3 className="text-md font-medium text-stone-700 mb-3">Add New Location</h3>
        <div className="flex gap-3 flex-wrap">
          <div className="flex-grow min-w-[200px]">
            <input
              type="text"
              value={newLocation.name}
              onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
              placeholder="Location name"
              className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <div>
            <select
              value={newLocation.type}
              onChange={(e) => setNewLocation({ 
                ...newLocation, 
                type: e.target.value as 'classroom' | 'restroom' | 'building' | 'other',
                buildingId: e.target.value === 'classroom' ? newLocation.buildingId : undefined
              })}
              className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="classroom">Classroom</option>
              <option value="restroom">Restroom</option>
              <option value="building">Building</option>
              <option value="other">Other</option>
            </select>
          </div>
          {newLocation.type === 'classroom' && (
            <div>
              <select
                value={newLocation.buildingId || ''}
                onChange={(e) => setNewLocation({ ...newLocation, buildingId: e.target.value })}
                className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="">Select Building</option>
                {buildings.map(building => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>
          )}          <button
            onClick={handleAddLocation}
            disabled={!newLocation.name.trim() || (newLocation.type === 'classroom' && !newLocation.buildingId) || isAdding}
            className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isAdding ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Adding...
              </>
            ) : (
              'Add Location'
            )}
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="mb-4 flex gap-4 items-center">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search locations..."
            className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-stone-400" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)}
            className="px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="all">All Types</option>
            <option value="classroom">Classrooms</option>
            <option value="restroom">Restrooms</option>
            <option value="building">Buildings</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>
      
      {/* Locations list */}
      <div className="max-h-[60vh] overflow-y-auto">
        {filteredLocations.length === 0 ? (
          <p className="text-center text-stone-500 py-8">
            {locations.length === 0 
              ? "No locations created yet. Add your first location above."
              : "No locations match your search criteria."}
          </p>
        ) : (
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-stone-700 uppercase bg-stone-100">
              <tr>
                <th className="px-4 py-2 rounded-tl-lg">Name</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Building</th>
                <th className="px-4 py-2 rounded-tr-lg">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLocations.map(location => (
                <tr key={location.id} className="hover:bg-stone-50">
                  {editingLocation?.id === location.id ? (
                    <td className="px-4 py-3 border-b border-stone-200">
                      <input
                        type="text"
                        value={editingLocation.name}
                        onChange={(e) => setEditingLocation({
                          ...editingLocation,
                          name: e.target.value
                        })}
                        className="w-full px-3 py-1 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </td>
                  ) : (
                    <td className="px-4 py-3 border-b border-stone-200">{location.name}</td>
                  )}
                  
                  {editingLocation?.id === location.id ? (
                    <td className="px-4 py-3 border-b border-stone-200">
                      <select
                        value={editingLocation.type}
                        onChange={(e) => setEditingLocation({
                          ...editingLocation,
                          type: e.target.value as 'classroom' | 'restroom' | 'building' | 'other',
                          buildingId: e.target.value === 'classroom' ? editingLocation.buildingId : undefined
                        })}
                        className="px-2 py-1 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="classroom">Classroom</option>
                        <option value="restroom">Restroom</option>
                        <option value="building">Building</option>
                        <option value="other">Other</option>
                      </select>
                    </td>
                  ) : (
                    <td className="px-4 py-3 border-b border-stone-200 capitalize">
                      {location.type}
                    </td>
                  )}

                  {editingLocation?.id === location.id ? (
                    <td className="px-4 py-3 border-b border-stone-200">
                      {editingLocation.type === 'classroom' && (
                        <select
                          value={editingLocation.buildingId || ''}
                          onChange={(e) => setEditingLocation({
                            ...editingLocation,
                            buildingId: e.target.value
                          })}
                          className="px-2 py-1 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">Select Building</option>
                          {buildings.map(building => (
                            <option key={building.id} value={building.id}>
                              {building.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                  ) : (
                    <td className="px-4 py-3 border-b border-stone-200">
                      {location.type === 'classroom' && location.buildingId && (
                        buildings.find(b => b.id === location.buildingId)?.name || 'Unknown Building'
                      )}
                    </td>
                  )}
                  
                  <td className="px-4 py-3 border-b border-stone-200">
                    {editingLocation?.id === location.id ? (                      <div className="flex gap-2">
                        <button
                          onClick={handleUpdateLocation}
                          disabled={isUpdating}
                          className="px-2 py-1 bg-green-600 text-white text-xs rounded disabled:bg-green-400 flex items-center gap-1"
                        >
                          {isUpdating ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                              Saving...
                            </>
                          ) : (
                            'Save'
                          )}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={isUpdating}
                          className="px-2 py-1 bg-stone-400 text-white text-xs rounded disabled:bg-stone-300"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleStartEdit(location)}
                          className="p-1 text-amber-600 hover:text-amber-800"
                          title="Edit Location"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onMoveOnMap(location.id)}
                          className="p-1 text-blue-600 hover:text-blue-800"
                          title="Move on Map"
                        >
                          <Map className="h-4 w-4" />
                        </button>                        <button
                          onClick={() => handleDeleteLocation(location.id)}
                          disabled={deletingLocationId === location.id}
                          className="p-1 text-red-600 hover:text-red-800 disabled:text-red-400"
                          title="Delete Location"
                        >
                          {deletingLocationId === location.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};